import { Capability, UserCapabilities, UserCapabilitiesCache } from './capabilityTypes';
import { Action } from './reducer';

// Cache keeps track of all requested capabilities
type UserId = string | number;
/**
 * The UserCache is used to provide a unique capabilities cache and queued request
 * for each user id passed to `useCapability`.
 */
export class UserCache {
  users: Record<UserId, { capabilitiesCache: CapabilitiesCache; queuedRequest: QueuedRequest }>;

  constructor() {
    this.users = {};
  }

  setCache(id: UserId) {
    this.users[id] = {
      capabilitiesCache: new CapabilitiesCache(),
      queuedRequest: new QueuedRequest(),
    };
  }

  getQueuedRequest(id?: UserId | null): QueuedRequest | null {
    if (!id) {
      return null;
    }
    if (!this.users[id]) {
      this.setCache(id);
    }
    return this.users[id].queuedRequest;
  }

  getCapabilities(id?: UserId | null): CapabilitiesCache | null {
    if (!id) {
      return null;
    }
    if (!this.users[id]) {
      this.setCache(id);
    }
    return this.users[id].capabilitiesCache;
  }
}

/**
 * The capabilities cache stores a map of all the requested capabilities and their
 * values. Before the API request for the capability completes, the value is
 * `queued`, indicating that the capability has been queued for request.
 */
export class CapabilitiesCache {
  currentCapabilities: UserCapabilitiesCache;
  constructor() {
    this.currentCapabilities = {};
  }

  update(capabilities: UserCapabilitiesCache) {
    this.currentCapabilities = { ...this.currentCapabilities, ...capabilities };
  }

  remove(toRemove: Capability[]) {
    for (let key of toRemove) {
      delete this.currentCapabilities[key];
    }
  }

  /**
   * Returns all the non-queued capabilities.
   */
  getAll() {
    let caps: UserCapabilities = {};
    for (let [key, val] of Object.entries(this.currentCapabilities)) {
      if (typeof val === 'boolean') caps[key] = val;
    }
    return caps;
  }
}

type Dispatch = (action: Action) => void;
/**
 * The QueuedRequest is used to store data from multiple invocations of `useCapability`
 * that is used when we ultimately execute the API request.
 */
class QueuedRequest {
  /** The timeoutId for the setTimeout function containing the API request. */
  timeoutId?: number;
  /** All the capabilities that are queued up for request */
  capabilities: Set<Capability>;
  /** All the dispatch functions we need to update on state changes */
  subscribers: Set<Dispatch>;

  constructor() {
    this.capabilities = new Set();
    this.subscribers = new Set();
  }

  clear() {
    this.timeoutId = undefined;
    this.capabilities = new Set();
    this.subscribers = new Set();
  }

  addCapabilities(c: Capability[]) {
    c.forEach((c) => this.capabilities.add(c));
  }

  addSubscriber(s: Dispatch) {
    this.subscribers.add(s);
  }

  getCapabilities() {
    return Array.from(this.capabilities);
  }

  getSubscribers() {
    return Array.from(this.subscribers);
  }

  setTimeoutId(timeoutId: number) {
    this.timeoutId = timeoutId;
  }

  clearTimeout() {
    clearTimeout(this.timeoutId);
  }
}
